import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MAX_RUNS, failedSlot, runCharts } from '../services/charts';
import { normalizeId } from '../utils/ids';

/** How many of Home's charts run before "Show more" (⚑ d13: Home stays fast). */
export const HOME_CHART_BATCH = 12;
/** A Home that comes back to the front re-runs its charts when their numbers are at least this old. */
export const HOME_STALE_MS = 5 * 60 * 1000;
/** The longest wait a refused request (429, 503) is waited out by itself, once. */
export const MAX_RETRY_WAIT_S = 60;

const NO_RESULTS = new Map();

const isVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/** What a pin asks the batch: its chart, in its CONTEXT (the collection whose items it counts). */
const runOf = (pin) => ({ chart_id: normalizeId(pin.target_id), collection_id: normalizeId(pin.collection_id) });
const keyOf = (pin) => `${normalizeId(pin._id)}:${normalizeId(pin.target_id)}:${normalizeId(pin.collection_id)}`;

/**
 * Home's charts' numbers (charts A7, plan §4.6 "Home stays fast", §5.5): the
 * page paints from GET /api/home first, then this asks ONE batch
 * (`POST /api/charts/run`) for the charts on screen — the first `count`
 * (HOME_CHART_BATCH, then more as "Show more" raises it) — each run the pin's
 * chart in the pin's own context. The server judges every run on its own, in
 * that context, so a chart the person lost since Home was read answers its
 * one `chart_unavailable` slot, never a name.
 *
 *   pins     GET /api/home's `charts`, in the person's order
 *   count    how many of them are shown (and so run)
 *
 * ONE REQUEST AT A TIME: the server holds one chart request per person in
 * flight (429 `budget: 'inFlight'`), so a batch never starts while one is
 * out; what is still missing is asked when the answer lands. A refused
 * request that names a wait of at most MAX_RETRY_WAIT_S is waited out and
 * asked again once; otherwise each chart gets the failed slot and says why in
 * its own frame. A Home re-read that keeps the same pins asks nothing; a tab
 * that comes back after HOME_STALE_MS re-runs what is shown, each chart
 * holding its last answer dimmed meanwhile — every chart shown, 24 to a
 * request, however many "Show more" has drawn.
 *
 * Returns { results: Map<pin row id, slot>, loading, ranAt, refresh }.
 */
export default function useChartPinsRun(pins, { count = HOME_CHART_BATCH, enabled = true } = {}) {
	const shown = useMemo(() => (Array.isArray(pins) ? pins : []).filter((pin) => pin && pin._id && pin.target_id && pin.collection_id).slice(0, count), [pins, count]);
	const [results, setResults] = useState(NO_RESULTS);
	// What has an answer: the ref is the truth a run decides by (written the
	// moment an answer lands); the state only re-renders, so what is missing
	// is asked once the answer is on screen — never twice for one answer.
	const [answered, setAnswered] = useState(() => new Set());
	const answeredRef = useRef(answered);
	const [loading, setLoading] = useState(false);
	const [ranAt, setRanAt] = useState(null);
	const flying = useRef(false);
	const alive = useRef(true);
	const lastRan = useRef(0);
	const retryTimer = useRef(null);
	const latest = useRef({ shown, enabled });
	latest.current = { shown, enabled };
	const runRef = useRef(null);

	const missingKey = shown.filter((pin) => !answered.has(keyOf(pin))).map(keyOf).join(',');

	const run = useCallback(async ({ all = false, retry = false } = {}) => {
		const now = latest.current;
		if (!alive.current || !now.enabled || (flying.current && !retry)) return;
		// A refresh asks again for EVERY chart shown (the A7 review's W1: it
		// asked the first 24 and left the rest on their first numbers): nothing
		// shown counts as answered any more, so this run asks the first 24 and
		// the missing-pins effect asks the rest, 24 at a time, one request out at
		// a time. Each chart keeps its last answer, dimmed, until its new one lands.
		if (all) {
			answeredRef.current = new Set();
			setAnswered(answeredRef.current);
		}
		const wanted = now.shown.filter((pin) => !answeredRef.current.has(keyOf(pin))).slice(0, MAX_RUNS);
		if (!wanted.length) return;
		flying.current = true;
		setLoading(true);
		let waiting = false;
		try {
			const answer = await runCharts(wanted.map(runOf));
			if (!alive.current) return;
			setResults((current) => {
				const next = new Map(current);
				wanted.forEach((pin, i) => next.set(normalizeId(pin._id), answer.results[i] || failedSlot(null, normalizeId(pin.target_id))));
				return next;
			});
			setRanAt(answer.ranAt || null);
		} catch (e) {
			if (!alive.current) return;
			const status = e && e.response ? e.response.status : null;
			const slot = failedSlot(e);
			const named = slot.error && typeof slot.error.retryAfter === 'number' ? slot.error.retryAfter : null;
			if (!retry && (status === 429 || status === 503) && named !== null && named <= MAX_RETRY_WAIT_S) {
				waiting = true;
				retryTimer.current = setTimeout(() => {
					retryTimer.current = null;
					if (runRef.current) runRef.current({ all, retry: true });
				}, Math.max(1, Math.ceil(named)) * 1000);
				return;
			}
			setResults((current) => {
				const next = new Map(current);
				wanted.forEach((pin) => next.set(normalizeId(pin._id), failedSlot(e, normalizeId(pin.target_id))));
				return next;
			});
		} finally {
			if (alive.current && !waiting) {
				lastRan.current = Date.now();
				const next = new Set(answeredRef.current);
				wanted.forEach((pin) => next.add(keyOf(pin)));
				answeredRef.current = next;
				setAnswered(next);
				flying.current = false;
				setLoading(false);
			}
		}
	}, []);
	runRef.current = run;

	// What is shown and has no answer yet: one batch for all of it.
	useEffect(() => {
		if (enabled && missingKey && !flying.current) run();
	}, [enabled, missingKey, loading, run]);

	// The tab coming back to the front, its numbers old.
	useEffect(() => {
		alive.current = true;
		const onVisibility = () => {
			if (!isVisible() || !lastRan.current) return;
			if (Date.now() - lastRan.current >= HOME_STALE_MS && runRef.current) runRef.current({ all: true });
		};
		document.addEventListener('visibilitychange', onVisibility);
		return () => {
			alive.current = false;
			if (retryTimer.current) clearTimeout(retryTimer.current);
			document.removeEventListener('visibilitychange', onVisibility);
		};
	}, []);

	const refresh = useCallback(() => run({ all: true }), [run]);

	return { results, loading, ranAt, refresh };
}
